import { motion } from "framer-motion";

export const Reveal = ({ children, delay = 0, className = "", y = 28 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3" data-testid="section-eyebrow">
    <span className="h-px w-8 bg-brand2" />
    <span className="eyebrow-label">{children}</span>
  </div>
);

export const RegistrationMark = ({ className = "", size = 44 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 44 44"
    fill="none"
    className={`animate-spin-slow ${className}`}
    aria-hidden="true"
  >
    <circle cx="22" cy="22" r="12" stroke="#174EA6" strokeOpacity="0.45" strokeWidth="1" />
    <circle cx="22" cy="22" r="6" stroke="#2563EB" strokeOpacity="0.6" strokeWidth="1" />
    <path d="M22 2v40M2 22h40" stroke="#174EA6" strokeOpacity="0.35" strokeWidth="1" />
    <circle cx="22" cy="22" r="1.5" fill="#2563EB" />
  </svg>
);

export const PageHero = ({ eyebrow, title, description, image }) => (
  <header className="relative overflow-hidden bg-canvas pt-32 pb-16 sm:pt-40 sm:pb-20" data-testid="page-hero">
    <div className="grid-lines absolute inset-0" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-12 lg:px-10">
      <div className="lg:col-span-7">
        <Reveal>
          <Eyebrow>{eyebrow}</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            {title}
          </h1>
        </Reveal>
        {description && (
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate sm:text-lg">{description}</p>
          </Reveal>
        )}
      </div>
      {image && (
        <Reveal delay={0.25} className="lg:col-span-5">
          <div className="relative overflow-hidden rounded-2xl border border-line bg-white shadow-sm">
            <img src={image} alt="" className="h-56 w-full object-cover sm:h-72 lg:h-80" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/25 to-transparent" />
          </div>
        </Reveal>
      )}
    </div>
  </header>
);

export const CTABanner = ({ title, text, quotePath = "/contact" }) => (
  <section className="relative overflow-hidden bg-brand" data-testid="cta-banner">
    <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/5" />
    <div className="pointer-events-none absolute -bottom-32 left-1/3 h-80 w-80 rounded-full bg-brand2/30 blur-3xl" />
    <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-16 sm:py-20 lg:flex-row lg:items-center lg:px-10">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">{title}</h2>
        <p className="mt-3 max-w-xl text-base text-white/80">{text}</p>
      </div>
      <a
        href={quotePath}
        data-testid="cta-banner-quote-btn"
        className="group inline-flex shrink-0 items-center gap-2.5 rounded-lg bg-white px-7 py-3.5 text-sm font-semibold text-brand shadow-sm transition-[background-color,box-shadow] duration-300 hover:bg-tint hover:shadow-md"
      >
        Request a Quote
        <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
      </a>
    </div>
  </section>
);
